import { Box, Stack, Text } from '@mantine/core'
import { IconButton } from '@mui/material'
import {
	BOOK_UNIVINI_ICON_BRAND,
	BOOK_UNIVINI_ICON_MUTED,
	BOOK_UNIVINI_ICON_SECONDARY,
	CloseFullIcon,
	DocumentDownloadIcon,
	MagicPenIcon,
} from 'src/components/BookUniVini/BookUniViniFileIcons'
import touchClasses from 'src/components/BookUniVini/BookUniViniTouch.module.scss'
import { formatFileSize } from 'src/utils/formatFileSize'
import { getFilenameFromUrl, openFilePreview } from 'src/utils/fileUrl'

interface DocFileCardProps {
	url: string
	// UD-614: the file's own name when it is known; otherwise the name is read from the address
	name?: string | null
	fileSize?: number | null
	onDownload?: () => void
	onRegenerate?: () => void
	onDelete?: () => void
	regenerating?: boolean
	showDelete?: boolean
}

function DocFileCard({
	url,
	name,
	fileSize,
	onDownload,
	onRegenerate,
	onDelete,
	regenerating = false,
	showDelete = false,
}: DocFileCardProps) {
	const filename = name || getFilenameFromUrl(url)
	const hasActions = Boolean(onRegenerate || onDownload || (showDelete && onDelete))

	return (
		<Box
			sx={{
				border: '1px solid #e6ebe7',
				borderRadius: 10,
				padding: 12,
				position: 'relative',
				background: '#fff',
			}}
		>
			{hasActions && (
				<Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 1.5 }}>
					{showDelete && onDelete && (
						<IconButton
							onClick={onDelete}
							size="small"
							aria-label="Remove document"
							className={touchClasses.touchIconButton}
							sx={{ color: BOOK_UNIVINI_ICON_MUTED, p: 0.5 }}
						>
							<CloseFullIcon size={20} />
						</IconButton>
					)}
					{onRegenerate && (
						<IconButton
							onClick={onRegenerate}
							size="small"
							disabled={regenerating}
							aria-label="Regenerate document"
							className={touchClasses.touchIconButton}
							sx={{ color: BOOK_UNIVINI_ICON_SECONDARY, p: 0.5 }}
						>
							<MagicPenIcon size={20} />
						</IconButton>
					)}
					{onDownload && (
						<IconButton
							onClick={onDownload}
							size="small"
							aria-label="Download document"
							className={touchClasses.touchIconButton}
							sx={{ color: BOOK_UNIVINI_ICON_BRAND, p: 0.5 }}
						>
							<DocumentDownloadIcon size={20} />
						</IconButton>
					)}
				</Box>
			)}

			<Stack spacing={6} pr={hasActions ? 88 : 0}>
				<Text size="sm" weight={600} lineClamp={1}>
					{filename}
				</Text>
				<Text size="xs" color="dimmed">
					{formatFileSize(fileSize)}
				</Text>
				<Text
					component="button"
					type="button"
					onClick={() => openFilePreview(url)}
					sx={{
						border: 'none',
						background: 'transparent',
						padding: 0,
						color: BOOK_UNIVINI_ICON_BRAND,
						fontWeight: 600,
						fontSize: 14,
						cursor: 'pointer',
						textAlign: 'left',
					}}
				>
					Preview
				</Text>
			</Stack>
		</Box>
	)
}

export default DocFileCard
